import { useState } from 'react';
import { useMerchantPayoutDestination } from './hooks/usePayoutDestinations';
import { RegisterPayoutDestinationModal } from './RegisterPayoutDestinationModal';
import { usePermissions, Permission } from '../../shared/hooks/usePermissions';
import Button from '../../shared/components/Button';
import { maskAccountNumber } from '../../shared/utils/mask';

interface Props {
  tenantId: string;
  businessName?: string;
  /** When true, the account number is masked (last 4 visible) — used on the merchant-facing Payouts dashboard. */
  maskAccount?: boolean;
}

/** Merchant split-payout destination — rendered on the merchant detail page. */
export function MerchantPayoutDestination({ tenantId, businessName, maskAccount = false }: Props) {
  const { hasPermission } = usePermissions();
  const { data: destination, isLoading, error } = useMerchantPayoutDestination(tenantId);
  const [showRegister, setShowRegister] = useState(false);

  const canManage = hasPermission(Permission.PAYOUT_DESTINATION_MANAGE);

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-foreground">Payout Destination</h2>
        {canManage && !destination && !isLoading && (
          <Button onClick={() => setShowRegister(true)}>Register</Button>
        )}
      </div>

      {isLoading && <p className="text-sm text-foreground">Loading payout destination…</p>}
      {error && <p className="text-sm text-red-500">Failed to load payout destination.</p>}

      {!isLoading && !error && !destination && (
        <p className="text-sm text-foreground">
          No payout destination registered. The merchant cannot receive split payouts until one is set up.
        </p>
      )}

      {!isLoading && !error && destination && (
        <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <div>
            <dt className="text-foreground">Business Name</dt>
            <dd className="font-medium text-foreground">{destination.businessName}</dd>
          </div>
          <div>
            <dt className="text-foreground">Status</dt>
            <dd>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${destination.isActive ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'}`}>
                {destination.isActive ? 'Active' : 'Inactive'}
              </span>
            </dd>
          </div>
          <div>
            <dt className="text-foreground">Account</dt>
            <dd className="font-medium text-foreground">{destination.accountName}</dd>
          </div>
          <div>
            <dt className="text-foreground">Account Number</dt>
            <dd className="font-medium text-foreground font-mono">{maskAccount ? maskAccountNumber(destination.accountNumber) : destination.accountNumber}</dd>
          </div>
          <div>
            <dt className="text-foreground">Settlement Bank</dt>
            <dd className="font-medium text-foreground">{destination.settlementBank || destination.bankCode}</dd>
          </div>
          <div>
            <dt className="text-foreground">Commission Rate</dt>
            <dd className="font-medium text-foreground">{(destination.commissionRate * 100).toFixed(1)}%</dd>
          </div>
          <div>
            <dt className="text-foreground">Type</dt>
            <dd className="font-medium text-foreground">{destination.destinationType?.replace('_', ' ') ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-foreground">BVN Verified</dt>
            <dd className="font-medium text-foreground">{destination.bvnVerified ? 'Yes' : 'No'}</dd>
          </div>
          <div>
            <dt className="text-foreground">Paystack Subaccount</dt>
            <dd className="font-medium text-foreground font-mono text-xs">{destination.paystackSubaccountCode ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-foreground">Flutterwave Subaccount</dt>
            <dd className="font-medium text-foreground font-mono text-xs">{destination.flutterwaveSubaccountId ?? '—'}</dd>
          </div>
        </dl>
      )}

      {showRegister && (
        <RegisterPayoutDestinationModal
          tenantId={tenantId}
          defaultBusinessName={businessName ?? destination?.businessName}
          onClose={() => setShowRegister(false)}
        />
      )}
    </div>
  );
}
